import { HttpStatusCode, PepperError } from '@pepperhq/pepper-error';
import { TypeGuard } from '../index.js';

let ECode = 'EVP';

export function initErrorPrefix(prefix: string) {
    ECode = prefix;
}

export enum ErrorCode {
    // common errors 1...99
    ValidationFailed = 1,
    AssertionError = 2,
    InvalidValue = 3,
    NotFound = 4,
    ApiCallError = 5,
    // 6
    CannotParse = 7,
    OutOfRange = 8,
    InvalidRequest = 9,
    InvalidSettings = 10,
    InternalError = 11,
    Timeout = 12,
    SyscallFailed = 13,
    SettingsCallFailed = 14,
    // 15

    Unauthorized = 30 // matches old NotAuthenticated

    // copy any new errors to other ErrorCode enums in the mono repo
}

// Standard errors

export class UnprocessableError extends PepperError {
    constructor(public errorCode: ErrorCode | number, errorText?: string, data?: any, isRetryable?: boolean) {
        // we can't reverse lookup the error code since it maybe from an error enum defined in a consuming app
        super(PepperError.makeStdCode(errorCode, ECode), HttpStatusCode.UNPROCESSABLE_ENTITY, errorText ? errorText : 'Cannot process', data, isRetryable);
        this.name = 'UnprocessableError';
    }
}
export class InternalError extends PepperError {
    constructor(public errorCode: ErrorCode | number, errorText?: string, data?: any, isRetryable?: boolean) {
        super(PepperError.makeStdCode(errorCode, ECode), HttpStatusCode.INTERNAL_SERVER_ERROR, errorText ? errorText : 'Internal error', data, isRetryable);
        this.name = 'InternalError';
    }
}

export class UnauthorizedError extends PepperError {
    constructor(public errorCode: ErrorCode | number, errorText?: string, data?: any, isRetryable?: boolean) {
        super(PepperError.makeStdCode(errorCode, ECode), HttpStatusCode.UNAUTHORIZED, errorText ? errorText : 'Not authorized', data, isRetryable);
        this.name = 'UnauthorizedError';
    }
}

export class NotFoundError extends PepperError {
    constructor(public errorCode: ErrorCode | number, errorText?: string, data?: any, isRetryable?: boolean) {
        super(PepperError.makeStdCode(errorCode, ECode), HttpStatusCode.NOT_FOUND, errorText ? errorText : 'Resource not found', data, isRetryable);
        this.name = 'NotFoundError';
    }
}
export class BadRequestError extends PepperError {
    constructor(public errorCode: ErrorCode | number, errorText?: string, data?: any, isRetryable?: boolean) {
        super(PepperError.makeStdCode(errorCode, ECode), HttpStatusCode.BAD_REQUEST, errorText ? errorText : 'Bad request', data, isRetryable);
        this.name = 'BadRequestError';
    }
}

export function assert(condition: any, assertionFailed?: string, errorConstructor: new (...args: any[]) => PepperError = InternalError): asserts condition {
    if (!condition) {
        throw new errorConstructor(ErrorCode.AssertionError, assertionFailed);
    }
}

export function validate<T extends ARG, ARG = unknown>(obj: ARG, typeGuard: TypeGuard<T, ARG>, error: string, errorCode: ErrorCode | number = ErrorCode.ValidationFailed): T {
    if (!typeGuard(obj)) {
        throw new UnprocessableError(errorCode, error); // FIXME should be BadRequestError ?
    }
    return obj;
}

export function validateReq<T extends ARG, ARG = unknown>(obj: ARG, typeGuard: TypeGuard<T, ARG>, error: string, errorCode: ErrorCode | number = ErrorCode.InvalidValue): T {
    if (!typeGuard(obj)) {
        throw new BadRequestError(errorCode, error);
    }
    return obj;
}

// type narrowing equivalent of validate
export function check<T extends ARG, ARG = unknown>(obj: ARG, typeGuard: TypeGuard<T, ARG>, error: string): asserts obj is T {
    if (!typeGuard(obj)) {
        throw new UnprocessableError(ErrorCode.ValidationFailed, error);
    }
}

// FIXME deprecate - use validateReq
export function validateValue<T>(obj: any, check: (t: T) => boolean, error: string, errorCode: ErrorCode | number = ErrorCode.InvalidValue): void {
    if (!check(obj)) {
        throw new BadRequestError(errorCode, error);
    }
}

export function validateAuthorized<T extends ARG, ARG = unknown>(obj: ARG, typeGuard: TypeGuard<T, ARG>, error: string = 'Unauthorized'): T {
    if (!typeGuard(obj)) {
        throw new UnauthorizedError(ErrorCode.Unauthorized, error);
    }
    return obj;
}
